/**
 * HubPageRouter - Conditional PageWrapper for Hub Pages
 *
 * This component determines whether a hub should be rendered within
 * the DevourPlay app shell (PUBLIC) or as a standalone landing page (PRIVATE).
 *
 * - PUBLIC hubs: Wrapped in PageWrapper with DevourPlaySidebar
 * - PRIVATE hubs: Rendered standalone with Header widget (@/widgets/Header)
 * - CUSTOM hubs: Route to brand-specific implementations (Monster, Polaris, etc.)
 *
 * Custom hubs live in /pages/hub/custom/{slug}/ and are registered below.
 * This keeps brand-specific code separate from the reusable template.
 *
 * React Query caches the hub data, so the subsequent useGetHub call
 * in HubPage will return cached data instantly (no double fetch).
 */

import React, { lazy, Suspense, useEffect } from "react";
import { Navigate, useParams, useSearchParams, useLocation } from "react-router";
import { useDispatch } from "react-redux";
import { useGetHub } from "@/hooks/hub/useGetHub";
import { CustomHubProps } from "./custom/types";
import PageWrapper from "@/components/routing/PageWrapper";
import { GlobalRewardNotifications } from "@/components/GlobalRewardNotifications";
import Skeleton from "react-loading-skeleton";
import { isDev } from "@/utils/isDev";
import { MinelabRttrLoadingFallback } from "./custom/minelab-rttr/components/MinelabRttrLoadingFallback";
import { FullThrottleLoadingFallback } from "./custom/polaris-cod-fullthrottle/components/FullThrottleLoadingFallback";
import { setRealmHandoff } from "@/redux/rttr/rttrActions";
import { useGetChallengeMemberStatus } from "@/hooks/useGetChallengeMemberStatus";
import { useGetChallengeRealmCountry } from "@/hooks/useGetChallengeRealmCountry";
import { useGetGeography } from "@/hooks/useGetGeography";

const RTTR_SLUG = "minelab-rttr";

/*
 * RTTR prewarm — fires the sign-up modal's data calls DURING the loader (this
 * component renders while useGetHub resolves AND the hub chunk downloads), so
 * member-status / realm-country / geography are already in the React Query cache
 * by the time the hub mounts. Without this they'd only start at hub-mount, after
 * the loader — lengthening it. Renders nothing.
 *
 * It captures the handshake off the URL into Redux (minimal — just enough to
 * enable the query hooks); the hub's own capture effect remains the authoritative
 * one (validation, URL strip, sessionStorage). Same React Query cache + query
 * keys as the hub, so this warms, never double-fetches.
 */
function RttrPrewarm(): null {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const realmUuid = searchParams.get("realm_uuid");
        if (!realmUuid) return;
        dispatch(setRealmHandoff({
            realmUuid,
            realmTs: searchParams.get("ts") ?? undefined,
            realmSig: searchParams.get("sig") ?? undefined,
        }));
    }, [searchParams, dispatch]);

    useGetChallengeMemberStatus(RTTR_SLUG);
    useGetChallengeRealmCountry(RTTR_SLUG, true);
    useGetGeography();
    return null;
}

/**
 * Registry of custom hub implementations.
 * Add new custom hubs here - they'll be lazy-loaded when accessed.
 *
 * DEV_ONLY_HUBS: Only available in development environments (not production).
 * Use this for brand mock-ups and demos that shouldn't be publicly accessible.
 */
const DEV_ONLY_HUBS: Record<string, React.LazyExoticComponent<React.FC<CustomHubProps>>> = {
    "polaris-cod": lazy(() => import("./custom/polaris-cod/PolarisCodHub")),
};

/**
 * Production-ready custom hubs (always available)
 */
const PRODUCTION_HUBS: Record<string, React.LazyExoticComponent<React.FC<CustomHubProps>>> = {
    "minelab-rttr": lazy(() => import("./custom/minelab-rttr/MinelabRttrHub")),
    "polaris-cod-fullthrottle": lazy(() => import("./custom/polaris-cod-fullthrottle/FullThrottleHub")),
};

// Merge hubs based on environment
const CUSTOM_HUBS: Record<string, React.LazyExoticComponent<React.FC<CustomHubProps>>> = {
    ...PRODUCTION_HUBS,
    ...isDev() ? DEV_ONLY_HUBS : {},
};

/**
 * Loading fallback for custom hub lazy loading.
 *
 * The `.hub-page*` classes this used came from `HubPage.scss`, which was
 * deleted with `HubPage.tsx` — the markup rendered unstyled. Skeletons carry
 * their own dimensions, so the wrapper only needs to stack and space them.
 */
const CustomHubLoadingFallback = () =>
    <div style={{display: "flex", flexDirection: "column", gap: "1rem", padding: "1rem"}}>
        <Skeleton height={60} />
        <Skeleton height={400} />
    </div>;
interface HubPageRouterProps {
    /** Optional slug override — used by VanitySlugResolver to avoid redirect */
    slugOverride?: string;
}

/*
 * Per-slug loading-fallback override map.
 * Custom hubs whose load surface MUST be brand-isolated register their own
 * loader here. Without an entry, slugs fall back to the generic
 * `CustomHubLoadingFallback` (DevourPlay-themed skeleton).
 *
 * RTTR (WU 28 fast-load): the generic loader briefly showed platform-themed
 * skeleton during useGetHub resolve + lazy-chunk download — visible
 * DevourPlay branding ahead of the Minelab brand experience. Replaced with
 * a self-contained Minelab loader (inline styles, no SCSS dependency, no
 * platform CSS-var inheritance) so every frame from tap-to-loaded is
 * Minelab-branded.
 *
 * **Onboarding new campaigns (Polaris × CoD, etc.):** every entry in
 * `web/src/customHubs/registry.ts` MUST have a matching entry here AND
 * in `CUSTOM_HUBS` (below). The registry holds the canonical brand
 * metadata; this map + CUSTOM_HUBS are the runtime React bindings.
 * Omitting an entry here leaves the new campaign rendering the generic
 * DevourPlay skeleton during chunk-download — silent brand-isolation
 * regression. Until/unless these maps are refactored to derive from the
 * registry directly, treat the three locations (registry + SLUG_LOADERS
 * + CUSTOM_HUBS) as a coupled set.
 */
const SLUG_LOADERS: Record<string, React.FC> = {
    "minelab-rttr": MinelabRttrLoadingFallback,
    "polaris-cod-fullthrottle": FullThrottleLoadingFallback,
};

export const HubPageRouter = ({ slugOverride }: HubPageRouterProps = {}) => {
    const { slug: paramSlug } = useParams<{ slug: string }>();
    const location = useLocation();
    const slug = slugOverride || paramSlug;

    /*
     * A registered campaign renders from CUSTOM_HUBS, not from a record (WU S.2),
     * and neither production campaign slug exists in `drops` — so the lookup can
     * only 404. Skipping it removes a wasted round trip and the error it pushes
     * into the store. The URL is untouched; only the fetch is.
     */
    const isRegisteredCampaign = Boolean(slug && CUSTOM_HUBS[slug]);
    const { data: hub, isLoading } = useGetHub(slug, !isRegisteredCampaign);

    /*
     * SLUG_LOADERS holds per-slug brand-isolated loaders for custom hubs
     * (minelab-rttr, etc.). Slugs without an entry use the generic
     * CustomHubLoadingFallback.
     */
    const LoadingFallback = slug && SLUG_LOADERS[slug] || CustomHubLoadingFallback;

    // Fire RTTR's sign-up data calls during the loader (see RttrPrewarm).
    const isRttr = slug === RTTR_SLUG;

    if (isLoading) {
        return <>{isRttr && <RttrPrewarm />}<LoadingFallback /></>;
    }

    // Check for custom hub implementation first
    const CustomHubComponent = slug ? CUSTOM_HUBS[slug] : undefined;

    /*
     * Custom-hub slugs are NEVER allowed to fall through to <PageWrapper> /
     * <HubPage>. PageWrapper renders the DevourPlay app shell — sidebar,
     * mobile nav, footer — which violates the brand-isolation contract
     * for partner-facing campaigns (RTTR, etc). That contract is upheld
     * structurally: everything below this point is inside the
     * `CustomHubComponent` branch, so no custom slug can reach PageWrapper.
     *
     * A REGISTERED CAMPAIGN DOES NOT REQUIRE A HUB RECORD (spine WU S.2).
     *
     * Being in CUSTOM_HUBS is the registration. This used to return the
     * loader whenever `hub` was falsy, and `useGetHub` sets no
     * `retry: false` — so a missing record left the loader up permanently
     * instead of degrading to the campaign. Rendering through is what lets
     * the two Hub records and `routes/hubs` retire on any schedule.
     *
     * The transient case the old guard covered (refetch, auth-state flip)
     * is now better: the campaign keeps rendering rather than flashing a
     * loader, and first paint is still the `isLoading` branch above.
     */
    if (CustomHubComponent) {
        /*
         * GlobalRewardNotifications mounts portal-style modals only
         * (MerchFulfillmentWonModal, PromoTokenWonModal, etc.) — no
         * platform chrome. Safe to wrap a custom hub: the brand-
         * isolation contract is preserved because nothing visible
         * renders until the user has an undismissed DistributedReward
         * (e.g. a wheel-spin MERCH win). PageWrapper normally mounts
         * this; custom hubs need it explicitly since they skip
         * PageWrapper entirely.
         */
        return (
            <>
                {isRttr && <RttrPrewarm />}
                <Suspense fallback={<LoadingFallback />}>
                    <GlobalRewardNotifications>
                        <CustomHubComponent hub={hub} />
                    </GlobalRewardNotifications>
                </Suspense>
            </>
        );
    }

    /*
     * No registered campaign for this slug.
     *
     * The generic `HubPage` used to render here. It served ZERO production
     * slugs: production holds 2 `brandhubs`, both registered in
     * `PRODUCTION_HUBS` above, so this branch was only ever reached by
     * unregistered dev records — the pre-migration copies `drop-in-hub-split`
     * preserved for rollback. It has been deleted with `routes/hubs`.
     *
     * A Drop is the surviving surface, so an unregistered slug belongs to
     * `/drop-in/:slug`, not here.
     */
    return <Navigate to={{pathname: `/drop-in/${slug}`, search: location.search}} replace />;
};

export default HubPageRouter;
